import { Component, OnInit } from "@angular/core";
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NotifyService } from "@core/notify/notify.service";
import {
  APP_LANGUAGES,
  AppLang,
  AppLangCode,
  DEFAULT_FLAG,
  DEFAULT_LANG,
  GLOBAL_LANG_KEY,
  findLang,
  normalizeLang,
} from "@core/i18n/app-languages";
import { SessionService } from "@core/state/session.service";
import { GoogleLoginButtonComponent } from "@features/auth/pages/login/components/google-login-button/google-login-button.component";
import { AppleLoginButtonComponent } from "@features/auth/pages/login/components/apple-login-button/apple-login-button.component";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideCheck, lucideChevronDown, lucideEye, lucideEyeOff, lucideLoader2 } from "@ng-icons/lucide";
import { HlmButtonImports } from "@spartan-ng/helm/button";
import { HlmDropdownMenuImports } from "@spartan-ng/helm/dropdown-menu";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "@core/state/local-storage.service";

@Component({
  selector: "app-login",
  standalone: true,
  providers: [provideIcons({ lucideCheck, lucideChevronDown, lucideEye, lucideEyeOff, lucideLoader2 })],
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmButtonImports,
    HlmDropdownMenuImports,
    GoogleLoginButtonComponent,
    AppleLoginButtonComponent,
    TranslateModule,
  ],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.scss",
})
export class LoginComponent implements OnInit {
  showPassword = false;
  loading = false;
  form!: FormGroup;
  private validationToastId?: string | number;

  private static readonly DEFAULT_REDIRECT = "/painel/financial/invoices";

  currentLang: AppLangCode = DEFAULT_LANG.code;
  selectedLang: AppLang = DEFAULT_LANG;

  readonly languages = APP_LANGUAGES;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notify: NotifyService,
    private readonly session: SessionService,
    private readonly fb: FormBuilder,
    private readonly t: TranslateService,
    private readonly storage: LocalStorageService,
  ) {}

  ngOnInit(): void {

    this.form = this.fb.group({
      email: ["", [
        Validators.required,
        Validators.email,
        Validators.maxLength(255),
        Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
      ]],
      password: ["", [Validators.required]],
    });


    const savedGlobal = this.storage.get<string>(GLOBAL_LANG_KEY);
    const candidate = normalizeLang(savedGlobal ?? this.t.currentLang ?? DEFAULT_LANG.code);
    this.currentLang = this.languages.some(l => l.code === candidate) ? candidate : DEFAULT_LANG.code;

    this.t.use(this.currentLang);
    this.storage.set(GLOBAL_LANG_KEY, this.currentLang);
    this.updateSelectedLang();

    this.handleTokenLogin();
  }

  changeLanguage(lang: AppLangCode) {
    if (lang === this.currentLang) return;
    const normalized = normalizeLang(lang);
    this.currentLang = normalized;
    this.t.use(normalized);
    this.storage.set(GLOBAL_LANG_KEY, normalized);
    this.updateSelectedLang();
  }

  private updateSelectedLang() {
    this.selectedLang = findLang(this.currentLang);
  }

  onFlagError(ev: Event) { (ev.target as HTMLImageElement).src = DEFAULT_FLAG; }

  get emailCtl() { return this.form.get("email"); }
  get passwordCtl() { return this.form.get("password"); }

  togglePasswordVisibility(): void { this.showPassword = !this.showPassword; }

  private isNetworkError(err: any): boolean {
    return !navigator.onLine ||
      (err && (err.status === 0 || (err.name === "HttpErrorResponse" && err.status === 0)));
  }

  private showRetryToast(messageKey: string, retryFn: () => void): void {
    const title = this.t.instant('LOGIN.ERROR_CONNECTION_TITLE');
    const suffix = this.t.instant('LOGIN.ERROR_TAP_RETRY');
    this.notify.error(
      `${this.t.instant(messageKey)} ${suffix}`,
      { title, duration: 8000, closeButton: true, onClick: () => retryFn() }
    );
  }

  async login(): Promise<void> {
    if (this.loading) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const errors: string[] = [];

      if (this.emailCtl?.hasError('required')) {
        errors.push(this.t.instant('LOGIN.EMAIL_REQUIRED'));
      } else if (this.emailCtl?.hasError('maxlength')) {
        errors.push(this.t.instant('LOGIN.EMAIL_TOO_LONG'));
      } else if (this.emailCtl?.hasError('email')) {
        errors.push(this.t.instant('LOGIN.EMAIL_INVALID'));
      } else if (this.emailCtl?.hasError('pattern')) {
        errors.push(this.t.instant('LOGIN.EMAIL_INVALID'));
      }

      if (this.passwordCtl?.hasError('required')) {
        errors.push(this.t.instant('LOGIN.PASSWORD_REQUIRED'));
      }

      if (this.validationToastId !== undefined) {
        this.notify.dismiss(this.validationToastId);
      }
      this.validationToastId = this.notify.error(
        errors.join(' ') || this.t.instant('LOGIN.FORM_INVALID')
      );
      return;
    }

    this.setLoading(true);
    try {
      const { email, password } = this.form.getRawValue();
      await this.session.login(email, password);
      this.navigateToReturnUrl();
    } catch (err: any) {
      if (this.isNetworkError(err)) {
        this.showRetryToast("LOGIN.ERROR_CONNECTION_GENERIC", () => this.login());
      } else {
        this.notify.apiError(err, "LOGIN.CREDENTIALS_INVALID");
      }
    } finally {
      this.setLoading(false);
    }
  }

  onGoogleCredential(token: string): void {
    this.session
      .loginWithGoogleCredential(token)
      .then(() => this.navigateToReturnUrl())
      .catch((err: any) => {
        if (this.isNetworkError(err)) {
          this.showRetryToast("LOGIN.ERROR_GOOGLE_CONNECTION", () => this.onGoogleCredential(token));
        } else {
          this.notify.apiError(err, "LOGIN.ERROR_GOOGLE");
        }
      });
  }

  onAppleCredential(credential: string): void {
    this.session
      .loginWithAppleCredential(credential)
      .then(() => this.navigateToReturnUrl())
      .catch((err) => this.notify.apiError(err, "LOGIN.ERROR_APPLE"));
  }

  private async handleTokenLogin(): Promise<void> {
    const token = this.route.snapshot.queryParamMap.get("token");
    if (!token) return;

    this.setLoading(true);
    try {
      await this.session.loginWithToken(token);
      this.router.navigate([LoginComponent.DEFAULT_REDIRECT]);
    } catch (err: any) {
      if (this.isNetworkError(err)) {
        this.showRetryToast("LOGIN.ERROR_TOKEN_CONNECTION", () => this.handleTokenLogin());
      } else {
        this.notify.apiError(err, "LOGIN.ERROR_TOKEN_INVALID");
      }
    } finally {
      this.setLoading(false);
    }
  }

  private navigateToReturnUrl(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get("returnUrl") ?? LoginComponent.DEFAULT_REDIRECT;
    this.router.navigateByUrl(returnUrl);
  }

  private setLoading(v: boolean): void {
    this.loading = v;
    v ? this.form.disable() : this.form.enable();
  }
}
